import React, { useEffect, useState, useCallback } from "react";
import PageTableLayout from "../../components/Pagetablelayout";
import axios from "axios";
import {
  Space,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  message,
} from "antd";

// Fixed set of ActivitiesExcursions categories. Update this list to change the
// available options everywhere they're used (form + table).
const CATEGORY_OPTIONS = [
  "Beach",
  "Safari",
  "Adventure",
  "Cultural",
  "Honeymoon",
  "Family",
  "Luxury",
  "Budget",
];

export default function ActivitiesExcursionsPage() {
  const [ActivitiesExcursions, setActivitiesExcursions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingActivitiesExcursions, setEditingActivitiesExcursions] = useState(null);
  const [form] = Form.useForm();

  const token = localStorage.getItem("jwt");
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchActivitiesExcursions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/activitiesexcursions/", {
        headers: authHeader,
      });
      setActivitiesExcursions(res.data || []);
    } catch (err) {
      console.error("Fetch ActivitiesExcursions failed:", err.response?.data || err.message);
      message.error("Failed to load ActivitiesExcursions");
    } finally {
      setLoading(false);
    }
  }, [token]); // token used indirectly via authHeader

  const fetchCountries = useCallback(async () => {
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/countries", {
        headers: authHeader,
      });
      setCountries(res.data || []);
    } catch (err) {
      console.error("Fetch countries failed:", err.response?.data || err.message);
      message.error("Failed to load countries");
    }
  }, [token]);

  const fetchCities = useCallback(async () => {
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/cities", {
        headers: authHeader,
      });
      setCities(res.data || []);
    } catch (err) {
      console.error("Fetch cities failed:", err.response?.data || err.message);
      message.error("Failed to load cities");
    }
  }, [token]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get("https://mdgholidaydreams.com/api/tags", {
        headers: authHeader,
      });
      setTags(res.data || []);
    } catch (err) {
      console.error("Fetch tags failed:", err.response?.data || err.message);
      message.error("Failed to load tags");
    }
  }, [token]);

  useEffect(() => {
    // run once on mount; functions are stable because of useCallback
    fetchActivitiesExcursions();
    fetchCountries();
    fetchCities();
    fetchTags();
  }, [fetchActivitiesExcursions, fetchCountries, fetchCities, fetchTags]);

  const handleAdd = () => {
    setEditingActivitiesExcursions(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const safeParseTags = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [raw];
      } catch {
        // fallback: comma separated string
        return raw.split(",").map((t) => t.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const handleEdit = (record) => {
    setEditingActivitiesExcursions(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      duration: record.duration,
      price: record.price,
      image: record.image,
      included: record.included,
      tags: safeParseTags(record.tags),
      country_id: record.country_id,
      city_id: record.city_id,
      category: record.category,
      featured: Number(record.featured) === 1 || record.featured === true,
    });
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // ensure tags are sent as array (backend expects JSON-encoded array)
      const payload = {
        ...values,
        tags: values.tags || [],
        featured: values.featured ? 1 : 0,
      };

      if (editingActivitiesExcursions) {
        await axios.put(`https://mdgholidaydreams.com/api/activitiesexcursions/?id=${editingActivitiesExcursions.id}`, payload, {
          headers: authHeader,
        });
        message.success("ActivitiesExcursions updated");
      } else {
        await axios.post("https://mdgholidaydreams.com/api/activitiesexcursions/", payload, {
          headers: authHeader,
        });
        message.success("ActivitiesExcursions created");
      }

      form.resetFields();
      setIsModalVisible(false);
      setEditingActivitiesExcursions(null);
      fetchActivitiesExcursions();
    } catch (err) {
      console.error("Save ActivitiesExcursions failed:", err.response?.data || err.message);
      message.error(err.response?.data?.message || "Failed to save ActivitiesExcursions");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingActivitiesExcursions(null);
    form.resetFields();
  };

  const selectedCountryId = Form.useWatch("country_id", form);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://mdgholidaydreams.com/api/activitiesexcursions/?id=${id}`, {
        headers: authHeader,
      });
      message.success("activitiesexcursions deleted");
      fetchActivitiesExcursions();
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      message.error("Failed to delete ActivitiesExcursions");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await axios.patch(`https://mdgholidaydreams.com/api/activitiesexcursions//activate?id=${id}`, {}, {
        headers: authHeader,
      });
      fetchActivitiesExcursions();
    } catch (err) {
      console.error("Toggle active failed:", err.response?.data || err.message);
      message.error("Failed to toggle activitiesexcursions status");
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Price", dataIndex: "price", key: "price" },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    {
      title: "Image Link",
      dataIndex: "image",
      key: "image",
      render: (url) => (url ? <a href={url} target="_blank" rel="noopener noreferrer">{url}</a> : "—"),
    },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      render: (val) => (Number(val) === 1 || val === true ? "Yes" : "No"),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active) => (Number(active) === 1 || active === true ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleEdit(record)}>Edit</Button>
          <Button danger onClick={() => handleDelete(record.id)}>Delete</Button>
          <Button onClick={() => handleToggleActive(record.id)}>
            {Number(record.active) === 1 || record.active === true ? "Deactivate" : "Activate"}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTableLayout
        breadcrumb={["Admin", "ActivitiesExcursions"]}
        title="Manage ActivitiesExcursions"
        addButtonLabel="Add ActivitiesExcursions"
        onAdd={handleAdd}
        columns={columns}
        dataSource={ActivitiesExcursions}
        loading={loading}
        onRefresh={fetchActivitiesExcursions}
      />

      <Modal
        title={editingActivitiesExcursions ? "Edit ActivitiesExcursions" : "Add New ActivitiesExcursions"}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description" rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="duration" label="Duration" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="price" label="Price (USD)" rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="image" label="Image Link (https://...)" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select placeholder="Select category" showSearch optionFilterProp="children">
                  {CATEGORY_OPTIONS.map((cat) => (
                    <Select.Option key={cat} value={cat}>
                      {cat}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label="Tags" rules={[{ required: true }]}>
                <Select mode="multiple" placeholder="Select tags" showSearch optionFilterProp="children">
                  {tags.map((tag) => (
                    <Select.Option key={tag.id} value={tag.name}>
                      {tag.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="country_id" label="Country" rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={() => form.setFieldsValue({ city_id: undefined })}
                >
                  {countries.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="city_id" label="City" rules={[{ required: true }]}>
                <Select showSearch placeholder="Select city" optionFilterProp="children">
                  {cities
                    .filter((city) => Number(city.country_id) === Number(selectedCountryId))
                    .map((city) => (
                      <Select.Option key={city.id} value={city.id}>
                        {city.name}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item name="included" label="What’s Included" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="e.g. Flights, Accommodation, Meals" />
              </Form.Item>

              <Form.Item name="featured" label="Featured ActivitiesExcursions" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}